import {
  AfterViewChecked,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DtoFormData, FormName, Language } from '@common';
import { FormDataService, I18nService } from '../../../../core';
import { MarkdownPipe } from '../../../../shared';
import { CvDialogComponent } from '../../cv-dialog/cv-dialog.component';
import { CvDialogParams } from '../../cv-dialog/cv-dialog.params';

@Component({
  selector: 'app-content-content',
  imports: [MarkdownPipe],
  templateUrl: './content-content.component.html'
})
export class ContentContentComponent implements AfterViewChecked, OnDestroy, OnChanges {
  /* eslint-disable @typescript-eslint/no-unsafe-call */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  /* eslint-disable @typescript-eslint/no-unsafe-member-access */
  /* eslint-disable @typescript-eslint/no-unsafe-assignment */

  //#region @Input/@Output/@ViewChild -----------------------------------------
  @Input({ required: true }) content!: string;
  //#endregion

  //#region Private Fields ----------------------------------------------------
  private readonly dialog: MatDialog;
  private readonly dialogRegex: RegExp;
  private readonly elementRef: ElementRef;
  private readonly formDataService: FormDataService;
  private readonly i18nService: I18nService;
  private readonly renderer: Renderer2;
  private readonly router: Router;
  private readonly routerRegex: RegExp;
  private anchorsProcessed: boolean;
  private readonly unlisteners: Array<() => void>;
  //#endregion

  //#region Constructor & C° --------------------------------------------------
  public constructor() {
    // --- Dependency injection ---
    this.dialog = inject(MatDialog);
    this.elementRef = inject(ElementRef);
    this.formDataService = inject(FormDataService);
    this.i18nService = inject(I18nService);
    this.router = inject(Router);
    this.renderer = inject(Renderer2);

    // --- initialize ---
    this.routerRegex = /\/(\S+)\?content=(\S+)(&target=router)/;
    this.dialogRegex = /\/(\S+)\?dialog=(\S+)&language=(\S+)/;
    this.unlisteners = new Array<() => void>();
    this.anchorsProcessed = false;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['content']) {
      this.anchorsProcessed = false;
    }
  }

  public ngAfterViewChecked(): void {
    if (!this.anchorsProcessed) {
      this.processAnchors();
      this.anchorsProcessed = true;
    }
  }

  public ngOnDestroy(): void {
    this.cleanupListeners();
  }
  //#endregion

  //#endregion private methods ------------------------------------------------
  private processAnchors(): void {
    // find all anchors. If they have target=router or a query parameter dialog then attach click event listener to them
    const anchors = this.elementRef.nativeElement.querySelectorAll('a');
    this.cleanupListeners();
    anchors.forEach((anchor: any) => {
      if (anchor.attributes) {
        const routerMatches = this.routerRegex.exec(anchor.getAttribute('href'));
        if (routerMatches) {
          anchor.setAttribute('href', `/${routerMatches[1]}?content=${routerMatches[2]}`);
          const unlistener = this.renderer.listen(
            anchor,
            'click',
            this.clickRouterEvent.bind(this, routerMatches[1], routerMatches[2])
          );
          this.unlisteners.push(unlistener);
        } else {
          const dialogMatches = this.dialogRegex.exec(anchor.getAttribute('href'));
          if (dialogMatches) {
            const url = new URL(anchor.getAttribute('href'), 'http://test');
            const dialogName = url.searchParams.get('dialog');
            const brand = url.searchParams.get('brand') || 'dec';
            const language = url.searchParams.get('language');
            anchor.setAttribute('href', '');
            const unlistener = this.renderer.listen(
              anchor,
              'click',
              this.clickDialogEvent.bind(this, dialogMatches[1], dialogName, language, brand)
            );
            this.unlisteners.push(unlistener);
          }
        }
      }
    });
  }

  private cleanupListeners(): void {
    this.unlisteners.forEach((unlistener: () => void) => unlistener());
    this.unlisteners.length = 0;
  }

  private clickRouterEvent(route: string, queryParams: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    // here we have some strange behavior:
    // - query params value is `der-e-coach%2Fself-service`
    // - but router navigates to der-e-coach%252Fself-service
    // that is the reason for the call to decodeURIComponent
    void this.router.navigate([route], { queryParams: { content: decodeURIComponent(queryParams) } });
  }

  private clickDialogEvent(url: string, dialogName: FormName, language: Language, brand: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (dialogName == 'cv-dialog') {
      this.formDataService.getCvDialogData(this.i18nService.currentLanguage).subscribe((formData: DtoFormData) => {
        const dialogParams: CvDialogParams = {
          formData: formData.data,
          language: language,
          url: url,
          brand: brand
        };
        this.dialog.open(CvDialogComponent, {
          width: '600px',
          data: dialogParams
        });
      });
    }
  }
  //#endregion
}
